/**
 * The confirmation in front of every delete. The dialog says what is about to go; the person types
 * "delete" into the box — by hand: pasted or dropped text is refused — and only then does the
 * delete button work, by a click (Enter in the box does nothing). A stray or double click on a
 * trash icon can't delete anything on its own.
 *
 * The box starts empty each time the dialog opens. Close it from the caller once the delete has
 * gone through (or failed: the caller says why).
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode, SyntheticEvent } from "react";

import { CONFIRM_WORD, confirmWordTyped, isPastedInput } from "../../lib/confirmDelete";
import { Modal } from "./Modal";

export function ConfirmDelete({
  open,
  title,
  children,
  confirmLabel = "Delete",
  busyLabel = "Deleting…",
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  /** What is about to be deleted, and what follows from it. */
  children: ReactNode;
  confirmLabel?: string;
  busyLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal open={open} title={title} onClose={() => !busy && onClose()}>
      {/* Mounted only while open, so the box is empty every time. */}
      <TypedConfirm confirmLabel={confirmLabel} busyLabel={busyLabel} busy={busy} onConfirm={onConfirm} onClose={onClose}>
        {children}
      </TypedConfirm>
    </Modal>
  );
}

function TypedConfirm({
  children,
  confirmLabel,
  busyLabel,
  busy,
  onConfirm,
  onClose,
}: {
  children: ReactNode;
  confirmLabel: string;
  busyLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [pasteRefused, setPasteRefused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const ready = confirmWordTyped(typed) && !busy;

  const refuse = (e: SyntheticEvent) => {
    e.preventDefault();
    setPasteRefused(true);
  };
  // onPaste / onDrop catch pasting and dropping; the native beforeinput (whose inputType React
  // doesn't pass on) catches the rest, e.g. a paste from a phone keyboard's menu.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const onBeforeInput = (e: InputEvent) => {
      if (!isPastedInput(e.inputType)) return;
      e.preventDefault();
      setPasteRefused(true);
    };
    input.addEventListener("beforeinput", onBeforeInput);
    return () => input.removeEventListener("beforeinput", onBeforeInput);
  }, []);

  return (
    <div className="space-y-4 text-sm text-gray-600">
      <div className="space-y-2">{children}</div>
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-gray-700">
          Type <span className="font-mono font-semibold text-red-700">{CONFIRM_WORD}</span> to confirm.
        </label>
        <input
          id={id}
          ref={inputRef}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onPaste={refuse}
          onDrop={refuse}
          disabled={busy}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-describedby={pasteRefused ? `${id}-paste` : undefined}
          data-testid="confirm-delete-input"
          className="w-full rounded-lg border px-3 py-2 text-gray-900 outline-hidden focus:border-red-500 focus:ring-1 focus:ring-red-500 disabled:bg-gray-50"
        />
        {pasteRefused && !ready && (
          <p id={`${id}-paste`} className="text-xs text-red-600">
            Pasting is turned off here: type the word.
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => ready && onConfirm()}
          disabled={!ready}
          data-testid="confirm-delete"
          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? busyLabel : confirmLabel}
        </button>
        <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border px-4 py-2 text-sm disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
}
